/* Copyright (c) 2020-present CloudIO Inc */

import {
  BooleanExpressionValue,
  Border,
  ExpressionOnlyValue,
  ExpressionValue,
  GridFlex,
  Height,
  IconValue,
  LookupValueType,
  Margin,
  Padding,
  PropertyDefinition,
  Responsive,
  ThemeColor,
} from 'componentregistry';
import React from 'react';
import LookupCodeChooserProperty from 'layout/Sidebar/panels/PropertiesView/property-editors/LookupCodeChooserProperty';
import LookupAttributeChooserProperty from 'layout/Sidebar/panels/PropertiesView/property-editors/LookupAttributeChooserProperty';
import CSVProperty from 'layout/Sidebar/panels/PropertiesView/property-editors/CSVProperty';
import ExpressionArrayProperty from 'layout/Sidebar/panels/PropertiesView/property-editors/ExpressionArrayProperty';
import { BGColor } from 'theme/useThemeColor';
import ControllerFunctionProperty from './ControllerFunctionProperty';
import PageIDChooserProperty from './PageIDChooserProperty';
import BGColorProperty from './BGColorProperty';
import BorderProperty from './BorderProperty';
import DataSourceAliasChooserProperty from './DataSourceAliasChooserProperty';
import DataSourceAliasProperty from './DataSourceAliasProperty';
import DataSourceChooserProperty from './DataSourceChooserProperty';
import AppUidChooserProperty from './AppUidChooserProperty';
import DefaultOperatorProperty from './DefaultOperatorProperty';
import ExpressionOnlyProperty from './ExpressionOnlyProperty';
import ExpressionProperty from './ExpressionProperty';
import FlexProperty from './FlexProperty';
import HeightProperty from './HeightProperty';
import IconSelectProperty from './IconSelectProperty';
import ItemIDInput from './ItemIDInput';
import ItemIDProperty from './ItemIDProperty';
import MarginProperty from './MarginProperty';
import NumberInputProperty from './NumberInputProperty';
import NumberSliderProperty from './NumberSliderProperty';
import OnOffExpressionProperty from './OnOffExpressionProperty';
import OnOffProperty from './OnOffProperty';
import PaddingProperty from './PaddingProperty';
import PropertyChooserProperty from './PropertyChooserProperty';
// eslint-disable-next-line import/no-cycle
import PropertyValueProperty from './PropertyValueProperty';
import RadioProperty from './RadioProperty';
import ResponsiveProperty from './ResponsiveProperty';
import SelectProperty from './SelectProperty';
import SingleSelectProperty from './SingleSelectProperty';
import TextProperty from './TextProperty';
import ThemeBGColorProperty from './ThemeBGColorProperty';
import { PropertyEditorProps } from './types';
import ViewAttributeChooserProperty, {
  ComboAttributeChooserProperty,
} from './ViewAttributeChooserProperty';
import MultiItemIDProperty from './MultiItemIDProperty';
import ColorExpressionProperty from './ColorExpressionProperty';
import OptionsProperty from './OptionsProperty';
import JSONProperty from './JSONProperty';
import { ImgAttributeChooserProperty } from './ImgAttributeChooserProperty';
import { itemWithDefaultValues } from '../../../../../registry/Components';
import TypescriptProperty from './TypescriptProperty';
import FunctionURIChooserProperty from './FunctionURIChooserProperty';
import { isViewOnlyDeveloper } from 'root/useUser';
import InvokeAttributeChooserProperty from './InvokeAttributeChooserProperty';
import MultiSelectOperatorProperty from './MultiSelectOperatorProperty';

export default function PropertyField({
  appUid,
  activeItemId,
  pageId,
  def,
  mgr,
  property,
  value,
  onChange,
  hmr,
  platform,
  isViewOnlyUser: readOnly,
}: PropertyEditorProps<PropertyDefinition, unknown>) {
  const isViewOnlyUser = readOnly || isViewOnlyDeveloper(platform, appUid);

  switch (def.type) {
    case 'ItemID':
      return (
        <ItemIDProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'MultiItemID':
      return (
        <MultiItemIDProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string[]}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'string':
      return property === 'itemId' ? (
        <ItemIDInput
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      ) : (
        <TextProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'options':
      return (
        <OptionsProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as ExpressionValue}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'expression':
      return (
        <ExpressionProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as ExpressionValue}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'ColorExpression':
      return (
        <ColorExpressionProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as ExpressionValue}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'expressionArray':
      return (
        <ExpressionArrayProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as ExpressionValue[]}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'expressionOnly':
      return (
        <ExpressionOnlyProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as ExpressionOnlyValue}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'boolean':
      return (
        <OnOffProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as boolean}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'booleanExpression':
      return (
        <OnOffExpressionProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as BooleanExpressionValue}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'number':
      return (
        <NumberSliderProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as number}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'numberInput':
      return (
        <NumberInputProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as number}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'grid':
      return (
        <FlexProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as GridFlex}
          platform={platform}
        />
      );
    case 'select':
      let useDropDown: boolean | undefined;
      if (typeof def.useDropDown === 'function') {
        const page = platform.getPage(appUid, pageId);
        const itemWithDefaults = itemWithDefaultValues(page[activeItemId]);
        useDropDown = def.useDropDown(itemWithDefaults, {
          appUid,
          pageId,
          itemId: activeItemId,
          mgr,
          platform,
        });
      } else {
        useDropDown = def.useDropDown;
      }

      return useDropDown ||
        (useDropDown === undefined && Object.keys(def.options).length > 3) ? (
        <SelectProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      ) : (
        <SingleSelectProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'radio':
      return (
        <RadioProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'iconselect':
      return (
        <IconSelectProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as IconValue}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'responsive':
      return (
        <ResponsiveProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as Responsive}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'padding':
      return (
        <PaddingProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as Padding}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'margin':
      return (
        <MarginProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as Margin}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'border':
      return (
        <BorderProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as Border}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'height':
      return (
        <HeightProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as Height}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'ThemeBGColor':
      return (
        <ThemeBGColorProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as ThemeColor}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'BGColor':
      return (
        <BGColorProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as BGColor}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'ViewAttributeChooser':
      return (
        <ViewAttributeChooserProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'InvokeAttributeChooser':
      return (
        <InvokeAttributeChooserProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'ComboAttributeChooser':
      return (
        <ComboAttributeChooserProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'ImgAttributeChooser':
      return (
        <ImgAttributeChooserProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'DataSourceChooser':
      return (
        <DataSourceChooserProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'LookupCodeChooser':
      return (
        <LookupCodeChooserProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as LookupValueType}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'LookupAttributeChooser':
      return (
        <LookupAttributeChooserProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'DataSourceAlias':
      return (
        <DataSourceAliasProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'DataSourceAliasChooser':
      return (
        <DataSourceAliasChooserProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'PropertyChooser': {
      return (
        <PropertyChooserProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    }
    case 'PropertyValue': {
      return (
        <PropertyValueProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    }
    case 'operator': {
      return (
        <DefaultOperatorProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    }
    case 'multiSelectOperator': {
      return (
        <MultiSelectOperatorProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    }
    case 'controllerFunction': {
      return (
        <ControllerFunctionProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    }
    case 'PageIDChooser': {
      return (
        <PageIDChooserProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    }
    case 'csv': {
      return (
        <CSVProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    }
    case 'json': {
      return (
        <JSONProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
          sampleData={def.sampleData}
        />
      );
    }
    case 'typescript': {
      return (
        <TypescriptProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    }
    case 'AppUidChooser':
      return (
        <AppUidChooserProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    case 'FunctionURIChooser':
      return (
        <FunctionURIChooserProperty
          activeItemId={activeItemId}
          appUid={appUid}
          def={def}
          hmr={hmr}
          key={`${property}-${activeItemId}`}
          mgr={mgr}
          onChange={onChange}
          pageId={pageId}
          property={property}
          value={value as string}
          platform={platform}
          isViewOnlyUser={isViewOnlyUser}
        />
      );
    default:
      return null;
  }
}
